<template>
  <ion-menu
    :content-id="contentId"
    :menu-id="menuId"
    side="start"
    type="overlay"
    :swipe-gesture="false"
  >
    <ion-content class="sidebar-content">
      <div class="sidebar-top px-5 py-4 border-b border-[var(--courses-border)]">
        <h2 class="text-xl tablet:text-subheading-xl font-semibold text-[var(--courses-ink)]">
          {{ programName }}
        </h2>
      </div>
      <nav class="p-3">
        <template v-for="part in parts" :key="part">
          <p
            v-if="parts.length > 1"
            class="px-3 py-2 text-xs tablet:text-caption-lg font-semibold uppercase tracking-wider text-[var(--courses-ink-subtle)]"
          >
            Part {{ part }}
          </p>
          <button
            v-for="lesson in sectionLessons(part)"
            :key="lesson.slug"
            type="button"
            class="lesson-item"
            :class="{ active: lesson.slug === currentSlug }"
            @click="selectLesson(lesson)"
          >
            <div class="flex items-start gap-3 text-left">
              <ion-icon :icon="bookOutline" class="lesson-icon" />
              <div class="flex-1 min-w-0">
                <p class="lesson-title">{{ lesson.title }}</p>
                <span class="lesson-number">Lesson {{ lessonNumber(lesson) }}</span>
              </div>
            </div>
          </button>
        </template>
      </nav>
    </ion-content>
  </ion-menu>
</template>

<script>
import { IonMenu, IonContent, IonIcon, menuController } from '@ionic/vue';
import { bookOutline } from 'ionicons/icons';
import { mapState, mapGetters } from 'vuex';
import directedNavigation from '@/mixins/DirectedNavigation';

export default {
  mixins: [directedNavigation],
  name: 'CoursesSidebar',
  components: {
    IonMenu,
    IonContent,
    IonIcon,
  },
  props: {
    contentId: {
      type: String,
      default: 'ion-content',
    },
    menuId: {
      type: String,
      default: 'courses-menu',
    },
  },
  data() {
    return {
      bookOutline,
    };
  },
  computed: {
    ...mapState('courses', ['lessons', 'programs']),
    ...mapGetters('courses', ['lessonsByProgram', 'lessonsBySection', 'lessonNumber']),
    currentSlug() {
      return this.$route.params.lessonId || null;
    },
    currentProgramSlug() {
      return (
        this.$route.params.programSlug ||
        this.$store.state.courses.currentProgramSlug ||
        this.programs[0]?.slug ||
        'next-steps-p1'
      );
    },
    programName() {
      const program = this.programs.find(p => p.slug === this.currentProgramSlug);
      if (program?.name) return program.name;
      return this.currentProgramSlug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    parts() {
      const programLessons = this.lessonsByProgram(this.currentProgramSlug);
      const set = new Set(programLessons.map(l => l.part).filter(p => p != null));
      if (!set.size) return [null];
      return Array.from(set).sort((a, b) => a - b);
    },
  },
  async mounted() {
    const tasks = [];
    if (!this.lessons.length) tasks.push(this.$store.dispatch('courses/fetchLessons'));
    if (!this.programs.length) tasks.push(this.$store.dispatch('courses/fetchPrograms'));
    if (tasks.length) {
      try {
        await Promise.all(tasks);
      } catch (error) {
        console.error('Failed to load data for sidebar:', error);
      }
    }
  },
  methods: {
    // Lessons listed for a section — excludes the student/leader guide entries,
    // which are surfaced via the carousel intro card, not as countable lessons.
    // The "Lesson N" label comes from the shared `courses/lessonNumber` getter
    // (the course-wide position), not the loop index — Next Steps is one
    // ten-lesson course split across two program rows, so Part 2 continues at 6.
    sectionLessons(part) {
      // Only real lessons are listed/numbered; guides and the no-lesson record
      // (kind !== 'LESSON') are excluded.
      return this.lessonsBySection(this.currentProgramSlug, part).filter(l => l.kind === 'LESSON');
    },
    async selectLesson(lesson) {
      await menuController.close(this.menuId);
      if (lesson.slug === this.currentSlug) return;
      const target = `/courses/${lesson.programSlug}/${lesson.slug}`;
      if (this.currentSlug) {
        // Lesson → lesson is a lateral jump: swap the reader in place rather than
        // stacking every lesson the user browsed through, but still animate it so
        // the jump doesn't hard-cut.
        this.goForwardReplacing(target);
      } else {
        this.$router.push(target);
      }
    },
  },
};
</script>

<style scoped>
.sidebar-content {
  --background: var(--courses-surface);
}
/* Reserve iOS status-bar / Android notch safe area on the top edge.
   Mirrors the pattern used in CoursesHeader and Header.vue. */
.sidebar-top {
  padding-top: max(1rem, env(safe-area-inset-top, 1rem));
}
.lesson-item {
  @apply w-full px-3 py-3 rounded-lg mb-1 transition-colors;
}
.lesson-item:hover,
.lesson-item:active {
  @apply bg-[var(--courses-surface-sunken)];
}
.lesson-item.active {
  background-color: var(--courses-selected);
}
.lesson-icon {
  @apply text-orange-500 text-xl shrink-0 mt-0.5;
}
.lesson-title {
  @apply text-base font-semibold text-[var(--courses-ink)] leading-snug mb-1.5;
}
.lesson-number {
  /* Ink flips light in dark mode, so the pill fill must flip with it. */
  @apply inline-block px-2 py-0.5 rounded text-xs font-medium text-[var(--courses-ink)] bg-[var(--courses-surface-sunken)];
}
</style>
